import React, { useEffect } from "react";
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
} from "../ui/table";
import { BookOpen } from "lucide-react";
import { useTranslation } from "react-i18next";
import { S_TEXT, TEXT } from "../../constant/textConstants";

const OrderDetails = ({ order }) => {
  const { t } = useTranslation();
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const totalAmount = order?.OrderItems.reduce(
    (sum, item) => sum + item.OrderPrice || 0 * item.Qty,
    0,
  );

  const formatCurrency = (amount, currencyCode) => {
    const finalAmount = amount || 0;
    const finalCurrencyCode =
      currencyCode && currencyCode?.trim() !== "" ? currencyCode : "USD";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: finalCurrencyCode,
    }).format(finalAmount);
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h2 className="text-2xl font-semibold">
              {t(TEXT?.ORDER)} {t(TEXT?.DETAILS)}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t(TEXT?.ORDER)} #{order.OrderNumber.trim()} -{" "}
              {t(TEXT?.PLACED_ON)} {formatDate(order?.OrderDate)}
            </p>
          </div>
          <div className="text-right">
            <p className="font-semibold">
              {t(TEXT?.TOTAL)} {t(TEXT?.AMOUNT)}
            </p>
            <p className="text-2xl font-bold text-primary">
              {formatCurrency(totalAmount, order?.OrderItems[0]?.CurrencyCode)}
            </p>
          </div>
        </div>
        <div>
          <h3 className="mb-2 font-semibold">
            {t(TEXT?.ORDER)} {t(TEXT?.ITEMS)}
          </h3>
          <div className="h-[300px] overflow-y-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20"></TableHead>
                  <TableHead className="w-[250px]">{t(TEXT?.ITEM)}</TableHead>
                  <TableHead>{S_TEXT?.SKU}</TableHead>
                  <TableHead>{S_TEXT?.ISBN_13}</TableHead>
                  <TableHead className="text-right">{S_TEXT?.QTY}</TableHead>
                  <TableHead className="text-right">{t(TEXT?.PRICE)}</TableHead>
                  <TableHead className="text-right">{t(TEXT?.TOTAL)}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {order?.OrderItems?.length > 0 ? (
                  order.OrderItems.map((item) => (
                    <TableRow key={item?.SkuNumber}>
                      <TableCell className="w-20 p-2">
                        <div className="h-16 max-h-full min-h-full w-12 min-w-full overflow-hidden rounded">
                          <img
                            loading="lazy"
                            className="h-full w-full object-cover"
                            alt={
                              `${item?.Title?.substring(0, 50)}...` ||
                              "Book cover"
                            }
                            src={
                              item?.ImageURL ||
                              "/icons/book/book_placeholder.svg"
                            }
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/icons/book/book_placeholder.svg";
                            }}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[250px] truncate font-medium">
                        {item?.Title}
                      </TableCell>
                      <TableCell>{item?.SkuNumber || "-"}</TableCell>
                      <TableCell>{item?.ISBN13 || "-"}</TableCell>
                      <TableCell className="text-right">{item?.Qty}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(item?.OrderPrice, item?.CurrencyCode)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(
                          item?.OrderPrice * item?.Qty,
                          item?.CurrencyCode,
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="py-12 text-center text-muted-foreground"
                    >
                      <div className="flex flex-col items-center justify-center gap-3">
                        <BookOpen className="h-12 w-12 text-gray-300" />
                        <p className="text-gray-500">
                          {t(TEXT?.NO_ITEMS_FOUND)}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
